import {
  Catalog_VariantPackGroupsQuery,
  Catalog_VariantPackGroupsQueryVariables,
} from '@/types';
import {
  CatalogCategoryType,
  ItemListOption,
} from '@/contexts/item-context-types';
import { gql } from '@apollo/client';
import { NULL_CATEGORY_VALUE } from '@/utils/category';
import { useItemQueryVariables } from './use-item-query-variables';
import { useMemo } from 'react';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useSupplierDynamicConfigValue } from './use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

export interface GroupItemsCategory {
  category: CatalogCategoryType;
  categoryItemCount: number;
  items: ItemListOption[];
}

export const CATALOG_VARIANT_PACK_GROUPS_QUERY = gql`
  query Catalog_VariantPackGroups(
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getSupplierCatalogVariantPackGroups(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      locale: $locale
    ) {
      variant_pack_group_id
      variant_pack_group_display_name
      variant_pack_group_item_count
      variant_pack_group_display_image_url
    }
  }
`;

const STARTS_WITH_SPECIAL_CHAR = /^[^\p{L}\p{N}]/u;

// Sort categories without a name last
export const compareByCategoryName = (
  a: Pick<CatalogCategoryType, 'name'>,
  b: Pick<CatalogCategoryType, 'name'>,
  specialCharsLast = false,
): number => {
  if (!a.name) {
    return 1;
  }
  if (!b.name) {
    return -1;
  }
  if (specialCharsLast) {
    const aSpecial = STARTS_WITH_SPECIAL_CHAR.test(a.name);
    const bSpecial = STARTS_WITH_SPECIAL_CHAR.test(b.name);
    if (aSpecial !== bSpecial) {
      return aSpecial ? 1 : -1;
    }
  }
  return a.name.localeCompare(b.name);
};

export const useCatalogVariantPackGroups = ({ skip }: { skip: boolean }) => {
  const itemQueryVariables = useItemQueryVariables();
  const { isGuestMode } = useUserContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const specialCharsLast = useSupplierDynamicConfigValue(
    'sort_categories_with_special_chars_last',
    false,
  );

  const { data, hasResponse } = useWrappedQuery<
    Catalog_VariantPackGroupsQuery,
    Catalog_VariantPackGroupsQueryVariables
  >(CATALOG_VARIANT_PACK_GROUPS_QUERY, {
    context: {
      anonymous: isGuestMode,
      noBatch: true,
    },
    skip: skip || !restaurantUUID || !supplierUUID,
    variables: {
      locale: itemQueryVariables.locale,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  return useMemo<GroupItemsCategory[] | null>(() => {
    if (!hasResponse) {
      return null;
    }

    return (data?.getSupplierCatalogVariantPackGroups ?? [])
      .map<GroupItemsCategory>((group) => ({
        category: {
          imageUrl: group.variant_pack_group_display_image_url,
          name: group.variant_pack_group_display_name || null,
          uuid: group.variant_pack_group_id || NULL_CATEGORY_VALUE,
        },
        categoryItemCount: group.variant_pack_group_item_count,
        items: [],
      }))
      .sort((a, b) =>
        compareByCategoryName(a.category, b.category, specialCharsLast),
      );
  }, [data, hasResponse, specialCharsLast]);
};
